import { useState } from 'react';
import { Form, Input, Modal, Radio, Select, notification } from 'antd';
import CustomButton from 'components/custom-button';
import type { StudioTeamMembersResult, StudioTeamRow } from '../contract';
import { addTeamMembers } from '../api';
import { mapStudioError } from '../studio-error';
import { TEAM_ADD_ROLE_OPTIONS, moveTeamConfirm } from '../labels';

/** Một thành viên Studio có thể xếp vào Nhóm nhảy (từ StudioMemberRow: key, tên, nhóm hiện tại). */
export interface AssignMember {
  key: string;
  name: string;
  team?: { id: number; name: string } | null;
}

export interface AssignTeamModalProps {
  open: boolean;
  studioId: number;
  /** Nhóm để chọn làm đích (chỉ nhóm ACTIVE hiện ra) — khi không cố định `team`. */
  teams?: Pick<StudioTeamRow, 'id' | 'name' | 'status'>[];
  /** Nhóm đích cố định (tab Nhóm nhảy → "Thêm người"). */
  team?: Pick<StudioTeamRow, 'id' | 'name'> | null;
  /** Người cố định (tab Thành viên → "Xếp vào Nhóm nhảy"). */
  members?: AssignMember[];
  /** Danh sách chọn người khi không cố định `members`. */
  memberOptions?: AssignMember[];
  onClose: () => void;
  onDone?: (result: StudioTeamMembersResult) => void;
}

interface FormValues {
  teamId?: number;
  keys?: string[];
  role: 'DANCER' | 'SUPPORT';
  note?: string;
}

/**
 * Xếp / chuyển người vào Nhóm nhảy (S19, mục 8.7.6). Giai đoạn 1 không kéo-thả. Người đang ở nhóm khác thì
 * hỏi "Chuyển từ <nhóm cũ> sang <nhóm mới>?" rồi gửi `moveIfInOtherTeam: true`. Kết quả có dòng lỗi thì
 * `notification.warning` liệt kê tên + câu lỗi từng dòng.
 */
const AssignTeamModal = ({
  open,
  studioId,
  teams = [],
  team,
  members,
  memberOptions = [],
  onClose,
  onDone,
}: AssignTeamModalProps) => {
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);
  const fixedMembers = !!members?.length;
  const targetTeamId = Form.useWatch('teamId', form) ?? team?.id;

  const activeTeams = teams.filter((t) => t.status === 'ACTIVE');
  const teamName = (id?: number) =>
    (team && team.id === id ? team.name : activeTeams.find((t) => t.id === id)?.name) || '';

  const handleClose = () => {
    if (saving) return;
    form.resetFields();
    onClose();
  };

  const submit = async (values: FormValues, people: AssignMember[], move: boolean) => {
    const teamId = team?.id ?? values.teamId;
    if (!teamId) return;
    setSaving(true);
    try {
      const result = await addTeamMembers(studioId, teamId, {
        keys: people.map((p) => p.key),
        role: values.role,
        moveIfInOtherTeam: move,
        note: values.note?.trim() || undefined,
      });
      const failed = (result?.results || []).filter((r) => !r.ok);
      const nameOf = (key: string) => people.find((p) => p.key === key)?.name || key;
      if (failed.length) {
        notification.warning({
          message: `Đã xếp ${result.added + result.moved}/${people.length} người vào ${teamName(
            teamId
          )}.`,
          description: (
            <div>
              {failed.map((r) => (
                <div key={r.key}>
                  {nameOf(r.key)}: {r.message || mapStudioError({ message: r.code })}
                </div>
              ))}
            </div>
          ),
          duration: 8,
        });
      } else {
        const moved = result?.moved ? `, chuyển ${result.moved} người từ nhóm khác` : '';
        notification.success({
          message: `Đã xếp ${result?.added ?? 0} người vào ${teamName(teamId)}${moved}.`,
        });
      }
      form.resetFields();
      onDone?.(result);
      onClose();
    } catch (err) {
      notification.error({ message: mapStudioError(err) });
    } finally {
      setSaving(false);
    }
  };

  const onFinish = (values: FormValues) => {
    const teamId = team?.id ?? values.teamId;
    const picked = fixedMembers
      ? members || []
      : memberOptions.filter((m) => (values.keys || []).includes(m.key));
    const people = picked.filter((p) => p.team?.id !== teamId);
    if (!teamId || !people.length) {
      notification.error({
        message: picked.length ? 'Những người đã chọn đang ở nhóm này.' : 'Chọn ít nhất một người.',
      });
      return;
    }
    const movers = people.filter((p) => p.team && p.team.id !== teamId);
    if (!movers.length) {
      submit(values, people, false);
      return;
    }
    const target = teamName(teamId);
    Modal.confirm({
      title:
        movers.length === 1
          ? moveTeamConfirm(movers[0].team?.name || '', target)
          : `Chuyển ${movers.length} người sang ${target}?`,
      content:
        movers.length > 1 ? (
          <div>
            {movers.map((m) => (
              <div key={m.key}>
                {m.name}: {moveTeamConfirm(m.team?.name || '', target)}
              </div>
            ))}
          </div>
        ) : undefined,
      okText: 'Chuyển',
      cancelText: 'Huỷ',
      onOk: () => submit(values, people, true),
    });
  };

  return (
    <Modal
      title={team ? `Thêm người vào ${team.name}` : 'Xếp vào Nhóm nhảy'}
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
      maskClosable={false}
      width={560}
    >
      {fixedMembers && (
        <p style={{ marginTop: 0 }}>
          {members?.map((m) => m.name).join(', ')}
          {members?.length === 1 && members[0].team ? ` · đang ở ${members[0].team.name}` : ''}
        </p>
      )}
      <Form
        form={form}
        layout="vertical"
        className="custom-form"
        initialValues={{ role: 'DANCER' }}
        onFinish={onFinish}
      >
        {!team && (
          <Form.Item
            label="Nhóm nhảy"
            name="teamId"
            rules={[{ required: true, message: 'Chọn Nhóm nhảy.' }]}
          >
            <Select
              placeholder="Chọn Nhóm nhảy"
              options={activeTeams.map((t) => ({ value: t.id, label: t.name }))}
              notFoundContent="Studio chưa có Nhóm nhảy."
            />
          </Form.Item>
        )}
        {!fixedMembers && (
          <Form.Item
            label="Thành viên"
            name="keys"
            rules={[{ required: true, type: 'array', min: 1, message: 'Chọn ít nhất một người.' }]}
          >
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              placeholder="Chọn thành viên của Studio"
              maxCount={50}
              options={memberOptions.map((m) => ({
                value: m.key,
                label: m.team ? `${m.name} (đang ở ${m.team.name})` : m.name,
                disabled: !!targetTeamId && m.team?.id === targetTeamId,
              }))}
            />
          </Form.Item>
        )}
        <Form.Item label="Vai trò" name="role">
          <Radio.Group options={TEAM_ADD_ROLE_OPTIONS} optionType="button" />
        </Form.Item>
        <Form.Item label="Ghi chú (không bắt buộc)" name="note">
          <Input.TextArea rows={2} maxLength={255} showCount />
        </Form.Item>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <CustomButton text="Huỷ" bgColor="primary-grey" onClick={handleClose} />
          <CustomButton text="Xếp vào nhóm" bgColor="secondary-green" htmlType="submit" loading={saving} />
        </div>
      </Form>
    </Modal>
  );
};

export default AssignTeamModal;
